<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2022 (original work) Open Assessment Technologies SA ;

    import { __ } from '@oat-sa-private/ui-core';
    import { onMount, onDestroy } from 'svelte';

    /**
     * Volume Visualizer component, consists of a doted line (number of dots can be set) that marks in blue the audio volume en activated.
     * @property {Number} [totalDots] - Number of dots that represent max volume, 25 set as default
     * @property {Boolean} [isMic] - Flag to state if the audio source is Mic.
     * @property {Object} [streamAudio] Mediastream object
     * @property {Object} [audioContext] AudioContext object
     * @property {Number} [fftSize] - value must be a power of 2 between 2^5 and 2^15. Represents the window size of the FFT, given in number of samples.
     * @property {Number} [minDecibels] - represents the maximum power value in the scaling range for the FFT analysis data, where 0 dB is the loudest possible sound
     * @property {Number} [maxDecibels] - represents the minimum power value in the scaling range for the FFT analysis data
     * @property {Number} [smoothingTimeConstant] - values expected between 0 - 1 (where 0 is no time averaging) It's an average between the current buffer and the last buffer the AnalyserNode processed (smoother set of value changes over time).
     * @property {Boolean} [isActive] - Flag to set whether functionality is active or inactive
     * @property {String} [title] - Text to be displayed before the volume visualizer
     */
    export let totalDots = 25;
    export let isMic;
    export let streamAudio;
    export let fftSize = 1024;
    export let minDecibels = -127;
    export let maxDecibels = 0;
    export let smoothingTimeConstant = 0.4;
    export let isActive = false;
    export let title = __('Input level');
    export let audioContext;

    let isMounted = false;
    let volumeInterval = null;
    let volumeCallback = null;
    let mediaElementSourceNode = null;
    let volumeVisualizer;
    let level;
    // Value range: 127 = analyser.maxDecibels - analyser.minDecibels;
    let max = 127;
    const step = max / totalDots;
    $: {
        if (streamAudio && mediaElementSourceNode && mediaElementSourceNode.mediaElement !== streamAudio) {
            mediaElementSourceNode = null;
        }
    }

    $: {
        if (isMounted) {
            if (isActive) {
                startVisualizer();
            } else {
                stopVisualizer();
            }
        }
    }

    onMount(() => {
        isMounted = true;
    });
    onDestroy(() => {
        stopVisualizer();
    });

    function startVisualizer() {
        stopVisualizer();
        let audioSource = null;
        if (isMic) {
            audioSource = audioContext.createMediaStreamSource(streamAudio);
        } else if (!mediaElementSourceNode) {
            mediaElementSourceNode = audioContext.createMediaElementSource(streamAudio);
        }
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = fftSize;
        analyser.minDecibels = minDecibels;
        analyser.maxDecibels = maxDecibels;
        analyser.smoothingTimeConstant = smoothingTimeConstant;
        if (isMic) {
            audioSource.connect(analyser);
        } else {
            mediaElementSourceNode.connect(analyser);
            analyser.connect(audioContext.destination);
        }

        const volumes = new Uint8Array(analyser.frequencyBinCount);
        volumeCallback = () => {
            analyser.getByteFrequencyData(volumes);
            const volumeSum = volumes.reduce((sum, volume) => sum + volume, 0);
            let averageVolume = volumeSum / volumes.length;
            level = Math.ceil(averageVolume / step);
        };
        // Updating every 100ms
        if (volumeCallback !== null && volumeInterval === null) {
            volumeInterval = setInterval(volumeCallback, 100);
        }
    }
    function stopVisualizer() {
        if (volumeInterval) {
            clearInterval(volumeInterval);
            volumeInterval = null;
            level = 0;
        }
    }
</script>

<style>
    div.dots-container {
        display: flex;
        justify-content: space-between;
        align-items: center;
        position: relative;
        gap: 1rem;
        & h5 {
            margin-bottom: var(--space-3x);
            font-size: var(--fontsize-heading);
            font-weight: var(--dir-font-weight);
            min-width: 13rem;
            font-weight: 700; /* even in rtl */
        }
    }
    div.dot {
        border-radius: 25px;
        min-width: 2rem;
        min-height: 2rem;
        background-color: var(--color-gs-graphical);
    }
    div.dots-container .active {
        background-color: var(--color-brand);
    }
</style>

<div class="dots-container" bind:this={volumeVisualizer}>
    <h5>{title}</h5>
    {#each { length: totalDots } as _, i}
        <div class="dot" class:active={i < level && level !== 0} />
    {/each}
</div>
